/**
 * Relative-time presets for the `created` filter chip.
 *
 * Presets are *live*: the URL stores the preset slug (e.g. `?created=last_1h`)
 * and the resolver runs every time `buildQueryFilters` runs. Reopening the
 * page tomorrow with the same URL shows tomorrow's last-hour window.
 */

import type { TimeRangePreset } from '@bastion/list-view';

function subHours(d: Date, h: number): Date {
  return new Date(d.getTime() - h * 60 * 60_000);
}

function subDays(d: Date, days: number): Date {
  return subHours(d, days * 24);
}

export const CREATED_PRESETS: TimeRangePreset[] = [
  {
    id: 'last_1h',
    label: 'Last 1 hour',
    resolve: (now) => ({ from: subHours(now, 1), to: now }),
  },
  {
    id: 'last_6h',
    label: 'Last 6 hours',
    resolve: (now) => ({ from: subHours(now, 6), to: now }),
  },
  {
    id: 'last_12h',
    label: 'Last 12 hours',
    resolve: (now) => ({ from: subHours(now, 12), to: now }),
  },
  {
    id: 'last_1d',
    label: 'Last 1 day',
    resolve: (now) => ({ from: subDays(now, 1), to: now }),
  },
  {
    id: 'last_2d',
    label: 'Last 2 days',
    resolve: (now) => ({ from: subDays(now, 2), to: now }),
  },
  {
    id: 'last_7d',
    label: 'Last 7 days',
    resolve: (now) => ({ from: subDays(now, 7), to: now }),
  },
  {
    id: 'last_30d',
    label: 'Last 1 month',
    resolve: (now) => ({ from: subDays(now, 30), to: now }),
  },
];

const PRESET_BY_ID = new Map(CREATED_PRESETS.map((p) => [p.id, p]));

export function findCreatedPreset(id: string): TimeRangePreset | undefined {
  return PRESET_BY_ID.get(id);
}

/**
 * `true` when the given chip value array looks like a preset (single,
 * non-ISO slug). Two-value arrays are treated as absolute ISO ranges.
 *
 * Exported because the chip pill, the submenu, and the query translator
 * all need to make the same preset-vs-absolute decision.
 */
export function isPresetChipValue(values: string[]): boolean {
  if (values.length !== 1) return false;
  const v = values[0]?.trim() ?? '';
  if (v.length === 0) return false;
  // ISO timestamps start with a 4-digit year + dash. Preset slugs use
  // snake_case lowercase. This is the cheapest disambiguation that
  // doesn't require parsing.
  return !/^\d{4}-/.test(v);
}
